'use client';
/**
 * Spec 147 §4.5 / D7 — the three account plan cards on `/pricing`
 * (Level 1 of the page), with ONE monthly / annual control above the row
 * instead of two prices printed in every card's paragraph.
 *
 * Persona: a prospect deciding "pay per use or a plan". The card says the
 * price tag for the chosen period, the credits a month, the storage, and
 * one button. Exactly one card carries the "Most popular" ring
 * (`popularId`). A plan whose live Stripe row does not sell at the
 * published price (`checkout_slug === null`) shows "Talk to us" rather
 * than a price it cannot sell; an annual price that has no Stripe price
 * yet falls back to the monthly checkout for that plan (the caller's
 * `annual_checkout`).
 *
 * The control is a native radiogroup: two `<input type="radio">`s in a
 * fieldset, so arrow keys move between them and a screen reader announces
 * "Monthly, radio button, 1 of 2" (WCAG 2.2 AA, spec 147 §4.7).
 */
import { useId, useState } from 'react';
import { snapshotPrice } from '../copy/pricing-snapshot.js';
import { cn } from '../lib/cn.js';
import { Icon } from '../primitives/icon.js';
import { PillButton } from '../primitives/pill-button.js';

export type PlanPeriod = 'monthly' | 'annual';

export interface PricingPlanCardData {
  id: string;
  label: string;
  price_usd_cents_monthly: number;
  price_usd_cents_annual_per_month: number;
  credits_per_month: number;
  storage_credits_gib: number;
  /** The live slug to check out with, or null when the plan cannot be sold at the published price. */
  checkout_slug: string | null;
  /** True when the live row also carries a yearly Stripe price. */
  annual_checkout: boolean;
}

export interface PricingPlansProps {
  plans: readonly PricingPlanCardData[];
  /** The one card with the "Most popular" ring; null → no ring anywhere (spec 116 AC 5: the badge is a row). */
  popularId: string | null;
  /** Where "Talk to us" goes (a plan that cannot be sold at the published price). */
  contactHref?: string;
  /** Initial period; the control changes it. */
  defaultPeriod?: PlanPeriod;
  className?: string;
}

function credits(n: number): string {
  return n.toLocaleString('en-US');
}

export function planHref(plan: PricingPlanCardData, period: PlanPeriod): string | null {
  if (!plan.checkout_slug) return null;
  const base = `/start?plan=${encodeURIComponent(plan.checkout_slug)}`;
  return period === 'annual' && plan.annual_checkout ? `${base}&period=year` : base;
}

export function PricingPlans({
  plans,
  popularId,
  contactHref = 'mailto:hi@connect0.ai',
  defaultPeriod = 'monthly',
  className,
}: PricingPlansProps) {
  const [period, setPeriod] = useState<PlanPeriod>(defaultPeriod);
  const groupId = useId();

  return (
    <div className={cn('flex flex-col gap-8', className)} data-pricing-plans>
      <fieldset className="mx-auto flex items-center gap-1 rounded-full border border-border-primary bg-surface-card-secondary p-1">
        <legend className="sr-only">Billing period</legend>
        {(
          [
            ['monthly', 'Monthly'],
            ['annual', 'Yearly, two months free'],
          ] as const
        ).map(([value, label]) => {
          const id = `${groupId}-${value}`;
          const active = period === value;
          return (
            <label
              key={value}
              htmlFor={id}
              className={cn(
                'cursor-pointer rounded-full px-4 py-1.5 font-label text-label-sm font-medium transition-colors',
                active ? 'bg-surface-action-2 text-text-on-action-1' : 'text-text-caption-1 hover:text-text-heading-1',
              )}
            >
              <input
                id={id}
                type="radio"
                name={`${groupId}-period`}
                value={value}
                checked={active}
                onChange={() => setPeriod(value)}
                className="sr-only"
              />
              {label}
            </label>
          );
        })}
      </fieldset>

      <div className="grid gap-6 md:grid-cols-3" data-plan-period={period}>
        {plans.map((plan) => {
          const popular = plan.id === popularId;
          const href = planHref(plan, period);
          const cents = period === 'annual' ? plan.price_usd_cents_annual_per_month : plan.price_usd_cents_monthly;
          return (
            <div
              key={plan.id}
              data-plan-card={plan.id}
              data-popular={popular ? '' : undefined}
              className={cn(
                'relative flex flex-col rounded-md border bg-surface-card-secondary p-8 transition-colors',
                popular ? 'border-2 border-accent-flame-500 shadow-sm' : 'border-border-primary',
              )}
            >
              {popular ? (
                <span className="absolute -top-3 left-8 inline-flex items-center rounded-full bg-surface-action-2 px-3 py-1 font-label text-label-xs font-semibold uppercase tracking-wide text-text-on-action-1">
                  Most popular
                </span>
              ) : null}
              <h3 className="heading-6 font-medium text-text-heading-1">{plan.label}</h3>
              <div className="mt-5">
                {href ? (
                  <>
                    <span className="heading-5 font-semibold text-text-heading-1">{snapshotPrice(cents)}</span>
                    <span className="ml-1 font-label text-label-md text-text-caption-1">
                      per account / month{period === 'annual' ? ', billed yearly' : ''}
                    </span>
                  </>
                ) : (
                  <span className="heading-6 font-medium text-text-heading-1">Talk to us</span>
                )}
              </div>
              <p className="mt-2 font-body text-body-sm text-text-subheading-1">
                {credits(plan.credits_per_month)} credits every month, shared by everyone in the account.
              </p>
              <ul className="mt-6 flex-1 space-y-3">
                <Feature>{plan.storage_credits_gib} GiB Storage Credits a month</Feature>
                <Feature>Spend caps, alerts &amp; full audit</Feature>
              </ul>
              <PillButton
                href={href ?? contactHref}
                fullWidth
                size="md"
                scheme={popular ? 'accent' : 'primary'}
                className="mt-8"
              >
                {href ? `Start with ${plan.label}` : 'Talk to us'}
              </PillButton>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Feature({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-success text-text-success"
      >
        <Icon name="check" size={20} className="size-3.5" />
      </span>
      <span className="font-body text-body-sm text-text-body">{children}</span>
    </li>
  );
}
